"use client";

import { parseRichText } from "@/configs/translations";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { motion } from "framer-motion";
import { ReactNode, useEffect, useRef, useState } from "react";

interface IVerticalTabsItem {
  id?: string
  title: string;
  subtitle: string;
  icon: ReactNode;
}

export interface IVerticalTabsProps<T = IVerticalTabsItem> {
  backgroundSelectionColor?: string;
  items: T[] | undefined;
  onChange?: (index: number, data: T) => void;
  className?: string
  subtitleClassName?: string
  i: string
}

export const VerticalTabs = ({
  backgroundSelectionColor = "white",
  items,
  onChange,
  className = "",
  subtitleClassName = "",
  i = ""
}: IVerticalTabsProps) => {
  const [activeTab, setActiveTab] = useState<number | null>(0);
  const [highlightStyle, setHighlightStyle] = useState({ top: 0, height: 48 });
  const subtitleRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (activeTab !== null) {
      const currentRef = subtitleRefs.current[activeTab];
      const height = 48 + (currentRef?.scrollHeight || 0);
      const top = activeTab * 48;

      setHighlightStyle({ top, height });
    }
  }, [activeTab]);

  if (items === undefined) return null;

  const onChangeTab = (index: number) => {
    setActiveTab(index);
    if (onChange && items[index]) onChange(index, items[index]);
  };

  return (
    <div className={`relative z-10 flex flex-col gap-4 ${className}`}>
      <div className="relative flex flex-col rounded-lg">
        <span
          className={`absolute -z-10 w-[100%] rounded-md duration-200`}
          style={{
            top: `${highlightStyle.top}px`,
            height: `${highlightStyle.height}px`,
            backgroundColor: backgroundSelectionColor,
          }}
        ></span>
        {items.map((item, index) => (
          <div key={`verticalTabs-${i}-${item.id}-${index}`} className="flex flex-col">
            <button
              onClick={() => onChangeTab(index)}
              className={`inline-flex h-12 items-center justify-start rounded-md px-3 text-sm font-medium duration-200 ${activeTab === index
                ? ""
                : `text-dark-5 hover:bg-gray-1 hover:text-dark hover:bg-[${backgroundSelectionColor}]`
                }`}
            >
              <span className="mr-4">{item.icon}</span>
              <p>{parseRichText(item.title, `verticalTabs-${i}-${item.id}-${index}`)}</p>
            </button>
            <motion.div
              initial={{ height: 0, opacity: 1 }}
              animate={{
                height: activeTab === index ? "auto" : 0,
              }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="overflow-hidden"
              ref={(el) => (subtitleRefs.current[index] = el) as any}
            >
              <div className={`px-3 py-2 text-sm rounded-b-md cursor-default text-gray-500 ${subtitleClassName}`}>
                {item.subtitle}
              </div>
            </motion.div>
          </div>
        ))}
      </div>
    </div>
  );
};
